import { useCallback, useEffect, useMemo, useState } from 'react';
import { CatalogueCard } from '../../components/catalogue/CatalogueCard';
import { DemCustomerFilters, type DemStatusFilter } from '../../components/dem/DemCustomerFilters';
import { DemItemPhotoModal } from '../../components/dem/DemItemPhotoModal';
import { useDemMasterDataRealtime } from '../../hooks/useDemMasterDataRealtime';
import {
  CATALOGUE_PAGE_SIZE,
  loadCatalogueItems,
  matchesCatalogueSearch,
  shouldShowCatalogueItem,
  type CatalogueItem,
  type CataloguePriceTier,
} from '../../lib/catalogueItems';
import type { DemItem } from '../../types/dem';

function scrollItemsToTop() {
  const body = document.querySelector('.dem-items-page .catalogue-page__body');
  if (body instanceof HTMLElement) {
    body.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  const page = document.querySelector('.page');
  if (page instanceof HTMLElement) {
    page.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function mergeItemUpdate(prev: CatalogueItem, updated: DemItem): CatalogueItem {
  return {
    ...prev,
    ...updated,
    outOfStock:
      updated.stock_status.trim().toLowerCase() === 'out of stock' || updated.inventory <= 0,
  };
}

export function DemItemsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrichError, setEnrichError] = useState<string | null>(null);
  const [pricesError, setPricesError] = useState<string | null>(null);
  const [enriching, setEnriching] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [priceTier, setPriceTier] = useState<CataloguePriceTier>('CATERING');
  const [blockedFilter, setBlockedFilter] = useState<DemStatusFilter>('all');
  const [showNoImage, setShowNoImage] = useState(false);
  const [items, setItems] = useState<CatalogueItem[]>([]);
  const [selected, setSelected] = useState<CatalogueItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
      setPage(1);
    }
    setError(null);
    setEnrichError(null);
    setPricesError(null);
    setEnriching(true);

    try {
      const { items: rows, enrichError: enrichErr, pricesError: priceErr } = await loadCatalogueItems({
        onPartial: (partial) => {
          setItems(partial);
          if (!silent) setLoading(false);
        },
      });
      setItems(rows);
      setEnrichError(enrichErr);
      setPricesError(priceErr);
      setSelected((prev) => {
        if (!prev) return null;
        return rows.find((row) => row.code === prev.code) ?? prev;
      });
    } catch (err) {
      setItems([]);
      setError(err instanceof Error ? err.message : 'Failed to load items.');
    } finally {
      setLoading(false);
      setEnriching(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useDemMasterDataRealtime({
    include: ['items'],
    onChange: () => {
      void load({ silent: true });
    },
  });

  useEffect(() => {
    setPage(1);
  }, [search, priceTier, blockedFilter, showNoImage]);

  const visible = useMemo(() => {
    let base = items.filter((item) => shouldShowCatalogueItem(item));
    if (blockedFilter === 'unblocked') base = base.filter((item) => !item.is_blocked);
    else if (blockedFilter === 'blocked') base = base.filter((item) => item.is_blocked);
    if (showNoImage) {
      base = base.filter((item) => !String(item.image_url ?? '').trim());
    }
    return base;
  }, [items, blockedFilter, showNoImage]);

  const filtered = useMemo(
    () => visible.filter((item) => matchesCatalogueSearch(item, search)),
    [visible, search],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / CATALOGUE_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * CATALOGUE_PAGE_SIZE;
    return filtered.slice(start, start + CATALOGUE_PAGE_SIZE);
  }, [filtered, safePage]);

  const from = filtered.length === 0 ? 0 : (safePage - 1) * CATALOGUE_PAGE_SIZE + 1;
  const to = Math.min(safePage * CATALOGUE_PAGE_SIZE, filtered.length);

  const emptyMessage = search.trim()
    ? 'No items match your search and filters.'
    : 'No active items found.';

  function openItem(item: CatalogueItem) {
    setSelected(item);
    setDetailOpen(true);
  }

  return (
    <div className="catalogue-page dem-items-page dem-items-page--floating">
      <div className="dem-floating-page-actions dem-floating-page-actions--end">
        <div className="dem-floating-page-actions__btns">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={loading && items.length === 0}
            onClick={() => void load()}
          >
            {loading && items.length === 0 ? 'Loading…' : enriching ? 'Syncing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="catalogue-page__sticky">
        <div className="dem-items-page__toolbar">
          <div className="dem-items-page__toolbar-leading">
            <div className="catalogue-page__tier-toggle" role="group" aria-label="Price group">
              <button
                type="button"
                className={`catalogue-page__tier-btn${priceTier === 'CATERING' ? ' catalogue-page__tier-btn--active' : ''}`}
                aria-pressed={priceTier === 'CATERING'}
                onClick={() => setPriceTier('CATERING')}
              >
                CATERING
              </button>
              <button
                type="button"
                className={`catalogue-page__tier-btn${priceTier === 'CATERING2' ? ' catalogue-page__tier-btn--active' : ''}`}
                aria-pressed={priceTier === 'CATERING2'}
                onClick={() => setPriceTier('CATERING2')}
              >
                CATERING2
                <span className="catalogue-page__tier-note">+10%</span>
              </button>
            </div>
            <div className="dem-items-page__filters">
              <DemCustomerFilters
                value={blockedFilter}
                onChange={setBlockedFilter}
                showNoImage={showNoImage}
                onShowNoImageChange={setShowNoImage}
              />
            </div>
          </div>
          <label className="catalogue-page__search">
            <span className="sr-only">Search items</span>
            <input
              type="search"
              className="catalogue-page__search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code, description, Chinese name or IPG"
              aria-label="Search items"
              disabled={loading && items.length === 0}
            />
          </label>
        </div>

        {error ? <p className="catalogue-page__error">{error}</p> : null}
        {enrichError ? (
          <p className="catalogue-page__warn">
            IPG / inventory from PowerBI unavailable: {enrichError}
          </p>
        ) : null}
        {pricesError ? (
          <p className="catalogue-page__warn">Price group prices unavailable: {pricesError}</p>
        ) : null}

        {!loading || items.length > 0 ? (
          <div className="catalogue-page__toolbar">
            <p className="catalogue-page__count">
              {filtered.length === 0
                ? 'No matches'
                : `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${filtered.length.toLocaleString()}`}
            </p>
          </div>
        ) : null}
      </div>

      <div className="catalogue-page__body">
        {loading && items.length === 0 ? (
          <div className="catalogue-grid catalogue-grid--skeleton" aria-busy>
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="catalogue-card catalogue-card--skeleton" />
            ))}
          </div>
        ) : pageItems.length === 0 && !error ? (
          <p className="catalogue-page__empty">{emptyMessage}</p>
        ) : (
          <div className="catalogue-grid">
            {pageItems.map((item) => (
              <CatalogueCard
                key={item.code}
                item={item}
                priceTier={priceTier}
                selected={detailOpen && selected?.code === item.code}
                onSelect={openItem}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && pageItems.length > 0 ? (
          <div className="catalogue-page__pager catalogue-page__pager--bottom">
            <button
              type="button"
              className="catalogue-page__pager-btn"
              disabled={safePage <= 1}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
                scrollItemsToTop();
              }}
            >
              Prev
            </button>
            <span className="catalogue-page__pager-status">
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              className="catalogue-page__pager-btn"
              disabled={safePage >= totalPages}
              onClick={() => {
                setPage((p) => Math.min(totalPages, p + 1));
                scrollItemsToTop();
              }}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      <DemItemPhotoModal
        item={selected}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdated={(updated) => {
          setItems((prev) =>
            prev.map((row) => (row.code === updated.code ? mergeItemUpdate(row, updated) : row)),
          );
          setSelected((prev) => (prev && prev.code === updated.code ? mergeItemUpdate(prev, updated) : prev));
        }}
      />
    </div>
  );
}
